import { CPTSlabForManualOption } from "../../../views/Orders/enums";
import { DeliverySupplierOption } from "../types";

function pickSourceSupplier(
  options: DeliverySupplierOption[] | undefined,
): DeliverySupplierOption | undefined {
  if (!options?.length) {
    return undefined;
  }
  return options.find((opt) => !opt.isAssociated) ?? options[0];
}

function pickAssociatedSupplier(
  options: DeliverySupplierOption[] | undefined,
): DeliverySupplierOption | undefined {
  if (!options?.length) {
    return undefined;
  }
  // No fallback to SIC — SP/SD require an associate branch when restricted.
  return options.find((opt) => opt.isAssociated);
}

export function isInstantManualCptSlab(
  cptSlab: CPTSlabForManualOption | number | undefined,
): boolean {
  return Number(cptSlab) === CPTSlabForManualOption.INSTANT_DELIVERY;
}

export function isAssociateOnlyManualCptSlab(
  cptSlab: CPTSlabForManualOption | number | undefined,
): boolean {
  const slab = Number(cptSlab);
  return (
    slab === CPTSlabForManualOption.STANDARD_DELIVERY ||
    slab === CPTSlabForManualOption.SELF_PICKUP
  );
}

/**
 * Locked fulfillment supplier for manual CPT slab when SIC is on
 * `manual_fulfillment_routing_suppliers`.
 * ID → SIC (source). SP/SD → AB only (undefined if no associate option).
 */
export function resolveManualFulfillmentSupplierForCptSlab(
  cptSlab: CPTSlabForManualOption,
  supplierOptions: DeliverySupplierOption[] | undefined,
): string | undefined {
  if (!supplierOptions?.length) {
    return undefined;
  }

  if (isInstantManualCptSlab(cptSlab)) {
    return pickSourceSupplier(supplierOptions)?.supplierId;
  }

  if (isAssociateOnlyManualCptSlab(cptSlab)) {
    return pickAssociatedSupplier(supplierOptions)?.supplierId;
  }

  return undefined;
}

/**
 * Destination supplier Select always shows the full IAM list (split + associated + rest).
 * CPT-slab routing may still pick a default id; it must not shrink the visible options.
 */
export function filterSupplierOptionsForManualCptSlab(
  _cptSlab: CPTSlabForManualOption | undefined,
  supplierOptions: DeliverySupplierOption[] | undefined,
  _isRestricted: boolean,
): DeliverySupplierOption[] {
  if (!supplierOptions?.length) {
    return [];
  }
  return supplierOptions;
}

export function isManualFulfillmentSupplierValidForCptSlab(params: {
  cptSlab: CPTSlabForManualOption | number | undefined;
  supplierId: string | null | undefined;
  supplierOptions: DeliverySupplierOption[] | undefined;
  isRestricted: boolean;
}): boolean {
  const { cptSlab, supplierId, supplierOptions, isRestricted } = params;
  if (!isRestricted || cptSlab == null) {
    return true;
  }
  if (!supplierId?.trim() || !supplierOptions?.length) {
    return false;
  }

  const selected = supplierOptions.find(
    (opt) => opt.supplierId === supplierId,
  );
  if (!selected) {
    return false;
  }

  if (isInstantManualCptSlab(cptSlab)) {
    return !selected.isAssociated;
  }
  if (isAssociateOnlyManualCptSlab(cptSlab)) {
    return selected.isAssociated;
  }
  return true;
}

export function getManualFulfillmentSupplierRestrictionMessage(
  cptSlab: CPTSlabForManualOption | number | undefined,
): string {
  if (isInstantManualCptSlab(cptSlab)) {
    return "Instant Delivery must use the split supplier (SIC).";
  }
  if (isAssociateOnlyManualCptSlab(cptSlab)) {
    return "Standard Delivery and Self Pickup must use the associate branch (AB).";
  }
  return "Selected fulfillment supplier is not allowed for this delivery type.";
}
